import { useEffect, useMemo, useState } from "react";
import { Navigate, Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Mountain, Flame, Award, Sparkles } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

// Keep in sync with /app/frontend/src/pages/OnboardingQuiz.jsx QUESTIONS.
const INTEREST_FIELDS = [
  { field: "interest_activity", label: "Favorite sport or activity",  placeholder: "Football, swimming, dance, hiking — what gets you moving?" },
  { field: "interest_hobby",    label: "Fun outside of school",       placeholder: "Video games, drawing, cooking, building things — what do you love to do?" },
  { field: "interest_subject",  label: "Favorite school subject",     placeholder: "Math, science, history, art — what class do you look forward to most?" },
  { field: "interest_career",   label: "Dream career",                placeholder: "Dream big! Astronaut, chef, teacher, athlete — anything goes!" },
  { field: "interest_explore",  label: "Dream place to explore",      placeholder: "Outer space, the ocean, ancient ruins, a big city — where would you go?" },
];

const interestCount = (s) =>
  INTEREST_FIELDS.reduce((n, f) => n + (((s?.[f.field] || "").trim().length > 0) ? 1 : 0), 0);

const Climbers = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newStudent, setNewStudent] = useState({ name: "", grade: "5" });
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [editing, setEditing] = useState(null); // climber being edited
  const [editForm, setEditForm] = useState({});
  const [savingInterests, setSavingInterests] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/students");
      setStudents(r.data);
    } catch (err) {
      toast.error("Could not load climbers");
    }
  };

  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user?.id]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-mm-navy/60">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;

  const addClimber = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/students", newStudent);
      toast.success(`${r.data.name} joined the expedition!`);
      setShowAdd(false);
      setNewStudent({ name: "", grade: "5" });
      await load();
      // Take them straight into the personalization quiz.
      navigate(`/climbers/${r.data.id}/onboarding`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add climber");
    } finally {
      setBusy(false);
    }
  };

  const removeClimber = async () => {
    if (!confirmRemove) return;
    setBusy(true);
    try {
      await api.delete(`/students/${confirmRemove.id}`);
      toast.success(`${confirmRemove.name} packed up`);
      setConfirmRemove(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove climber");
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (s) => {
    setEditing(s);
    const seeded = {};
    for (const f of INTEREST_FIELDS) seeded[f.field] = s?.[f.field] || "";
    setEditForm(seeded);
  };

  const saveInterests = async (e) => {
    e.preventDefault();
    if (!editing) return;
    // Build payload — send EVERY field (including empty strings) so a user can
    // clear an answer by emptying the input.
    const payload = {};
    for (const f of INTEREST_FIELDS) payload[f.field] = editForm[f.field] || "";
    // The PATCH endpoint requires at least one non-null field, but empty
    // strings ARE non-null and clear the value.
    setSavingInterests(true);
    try {
      const r = await api.patch(`/students/${editing.id}/interests`, payload);
      toast.success("Interests updated — future expeditions will use them.");
      setEditing(null);
      // Update local list with the refreshed climber.
      setStudents((list) => list.map((s) => (s.id === r.data.id ? { ...s, ...r.data } : s)));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save interests");
    } finally {
      setSavingInterests(false);
    }
  };

  const seatLimit = user?.seat_limit || 1;
  const seatsUsed = students.length;
  const seatsLeft = Math.max(0, seatLimit - seatsUsed);

  return (
    <div className="min-h-screen bg-mm-parchment bg-topo">
      <Navbar />
      <div className="container mx-auto py-10" data-testid="climbers-page">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel">Trail roster</div>
            <h1 className="font-heading text-4xl font-black text-mm-navy mt-2">Manage climbers</h1>
            <p className="text-mm-navy/70 mt-2">
              {seatsUsed} of {seatLimit === 999 ? "unlimited" : seatLimit} seat{seatLimit === 1 ? "" : "s"} used
              {seatsLeft > 0 && seatLimit !== 999 && ` · ${seatsLeft} open`}
            </p>
          </div>
          <button
            className="btn-trail btn-gold text-sm"
            onClick={() => setShowAdd(true)}
            disabled={seatsLeft === 0 && seatLimit !== 999}
            data-testid="climbers-add-btn"
          >
            <Plus size={16} strokeWidth={2.5} /> Add climber
          </button>
        </div>

        {seatsLeft === 0 && seatLimit !== 999 && (
          <div className="mt-5 rounded-xl bg-mm-gold/15 border-2 border-mm-gold/40 p-4 text-sm text-mm-navy/85" data-testid="climbers-seat-limit-banner">
            {seatLimit === 1 ? (
              <>Each climber needs their own seat — <Link to="/pricing" className="font-bold underline">upgrade to Summit Pass to add more explorers to your team</Link>.</>
            ) : (
              <>All seats are taken. <Link to="/pricing" className="font-bold underline">Add another seat</Link> to bring more climbers along.</>
            )}
          </div>
        )}

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {students.map((s) => {
            const filled = interestCount(s);
            return (
              <div key={s.id} className="stage-card p-5 flex flex-col" data-testid={`climber-card-${s.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-heading text-xl font-black text-mm-navy">{s.name}</div>
                    <div className="text-xs text-mm-navy/60 mt-0.5">Grade {s.grade}</div>
                  </div>
                  <button
                    onClick={() => setConfirmRemove(s)}
                    className="p-2 rounded-lg text-mm-navy/40 hover:text-red-600 hover:bg-red-50 transition-colors"
                    aria-label={`Remove ${s.name}`}
                    data-testid={`climber-remove-btn-${s.id}`}
                  >
                    <Trash2 size={16} strokeWidth={2.5} />
                  </button>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                  <Stat icon={<Mountain size={14} strokeWidth={2.5} />} label="AP" value={s.altitude_points || 0} />
                  <Stat icon={<Flame size={14} strokeWidth={2.5} />} label="Streak" value={`${s.compass_streak || 0}d`} />
                  <Stat icon={<Award size={14} strokeWidth={2.5} />} label="Badges" value={(s.badges || []).length} />
                </div>
                <div className="mt-4 pt-4 border-t border-mm-navy/10 flex items-center justify-between gap-2">
                  <div className="text-[11px] uppercase tracking-widest font-bold text-mm-steel" data-testid={`climber-interests-count-${s.id}`}>
                    Interests: {filled}/5
                  </div>
                  <button
                    onClick={() => openEdit(s)}
                    className="btn-trail btn-outline-navy text-xs"
                    data-testid={`climber-edit-interests-btn-${s.id}`}
                  >
                    <Sparkles size={12} strokeWidth={2.5} /> {filled === 0 ? "Add interests" : "Edit interests"}
                  </button>
                </div>
              </div>
            );
          })}
          {!students.length && (
            <div className="col-span-full text-mm-navy/60 py-10 text-center border-2 border-dashed border-mm-navy/15 rounded-xl">
              No climbers yet — add your first one to start the expedition.
            </div>
          )}
        </div>

        {/* Update My Interests sheet */}
        <Sheet open={!!editing} onOpenChange={(open) => !open && !savingInterests && setEditing(null)}>
          <SheetContent side="right" className="bg-mm-parchment border-l-2 border-mm-navy/10 w-full sm:max-w-md overflow-y-auto" data-testid="climber-interests-sheet">
            {editing && (
              <>
                <SheetHeader className="text-left">
                  <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-mm-steel">Update my interests</div>
                  <SheetTitle className="font-heading text-3xl font-black text-mm-navy">{editing.name}</SheetTitle>
                  <SheetDescription className="text-mm-navy/70">
                    Changes apply to every future expedition. Leave a field empty to remove that interest.
                  </SheetDescription>
                </SheetHeader>
                <form onSubmit={saveInterests} className="mt-6 space-y-5 pb-2" data-testid="climber-interests-form">
                  {INTEREST_FIELDS.map((f) => (
                    <div key={f.field}>
                      <label className="text-xs uppercase tracking-widest font-bold text-mm-steel">{f.label}</label>
                      <input
                        type="text"
                        maxLength={140}
                        value={editForm[f.field] || ""}
                        onChange={(e) => setEditForm({ ...editForm, [f.field]: e.target.value })}
                        placeholder={f.placeholder}
                        className="mt-1.5 w-full bg-white border-2 border-mm-navy/15 rounded-lg px-4 py-3 focus:outline-none focus:border-mm-steel focus:ring-2 focus:ring-mm-steel/30 placeholder:text-mm-navy/40"
                        data-testid={`climber-interests-input-${f.field}`}
                      />
                    </div>
                  ))}
                  <div className="pt-4 mt-2 -mx-6 px-6 border-t border-mm-navy/10 bg-mm-parchment sticky bottom-0 flex flex-wrap justify-end gap-2">
                    <button type="button" onClick={() => setEditing(null)} disabled={savingInterests} className="btn-trail btn-outline-navy text-sm">
                      Cancel
                    </button>
                    <button type="submit" disabled={savingInterests} className="btn-trail btn-gold text-sm" data-testid="climber-interests-save-btn">
                      {savingInterests ? "Saving…" : "Save interests"}
                    </button>
                  </div>
                </form>
              </>
            )}
          </SheetContent>
        </Sheet>

        {/* Add modal */}
        {showAdd && (
          <div className="fixed inset-0 bg-mm-navy/40 backdrop-blur-sm z-50 flex items-center justify-center px-4">
            <form onSubmit={addClimber} className="bg-white rounded-2xl p-8 max-w-md w-full" data-testid="climbers-add-modal">
              <h3 className="font-heading text-2xl font-black text-mm-navy">Add a climber</h3>
              <p className="text-sm text-mm-navy/70 mt-1">Next up: a 5-question personalization quiz so we can tune their expeditions.</p>
              <div className="mt-4 space-y-3">
                <input
                  placeholder="Name"
                  value={newStudent.name}
                  onChange={(e) => setNewStudent({ ...newStudent, name: e.target.value })}
                  required
                  className="w-full border-2 border-mm-navy/15 rounded-lg px-4 py-3 focus:outline-none focus:border-mm-steel"
                  data-testid="climbers-add-name-input"
                />
                <select
                  value={newStudent.grade}
                  onChange={(e) => setNewStudent({ ...newStudent, grade: e.target.value })}
                  className="w-full border-2 border-mm-navy/15 rounded-lg px-4 py-3 bg-white focus:outline-none focus:border-mm-steel"
                  data-testid="climbers-add-grade-select"
                >
                  <option value="K">Kindergarten</option>
                  {[...Array(12)].map((_, i) => <option key={i + 1} value={String(i + 1)}>Grade {i + 1}</option>)}
                </select>
              </div>
              <div className="mt-6 flex gap-3 justify-end">
                <button type="button" onClick={() => setShowAdd(false)} className="btn-trail btn-outline-navy text-sm">Cancel</button>
                <button type="submit" disabled={busy} className="btn-trail btn-gold text-sm" data-testid="climbers-add-submit-btn">
                  {busy ? "Adding..." : "Add & start quiz"}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Remove confirmation */}
        {confirmRemove && (
          <div className="fixed inset-0 bg-mm-navy/60 backdrop-blur-sm z-50 flex items-center justify-center px-4" data-testid="climbers-remove-modal">
            <div className="bg-white rounded-2xl p-7 max-w-md w-full border-2 border-red-200">
              <div className="text-xs uppercase tracking-widest font-bold text-red-700">Pack up climber</div>
              <h3 className="font-heading text-xl font-black text-mm-navy mt-1">
                Remove {confirmRemove.name}?
              </h3>
              <p className="text-sm text-mm-navy/70 mt-2">
                Their progress, badges, and expeditions stay on file in case you bring them back, but they'll be archived from your active roster.
              </p>
              <div className="mt-5 flex justify-end gap-2">
                <button onClick={() => setConfirmRemove(null)} disabled={busy} className="btn-trail btn-outline-navy text-sm">
                  Cancel
                </button>
                <button
                  onClick={removeClimber}
                  disabled={busy}
                  className="btn-trail text-sm bg-red-600 hover:bg-red-700 text-white border-red-600"
                  data-testid="climbers-remove-confirm-btn"
                >
                  {busy ? "Removing..." : "Yes, pack up"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Stat = ({ icon, label, value }) => (
  <div className="rounded-lg bg-mm-parchment py-2.5 px-1">
    <div className="text-mm-steel flex justify-center">{icon}</div>
    <div className="font-heading text-lg font-black text-mm-navy mt-0.5">{value}</div>
    <div className="text-[9px] uppercase tracking-widest text-mm-steel font-bold">{label}</div>
  </div>
);

export default Climbers;
